<script setup lang="ts">
/**
 * RouteErrorBoundary — last-resort UI guard around a <router-view> (2026-06-14).
 *
 * Problem it solves: before this, a render/lifecycle error in ANY descendant
 * component propagated to the root and Vue tore down the whole tree → the
 * entire screen went WHITE, with no message and nothing logged. A single bad
 * row (a non-string field that crashed a list formatter) took down the app.
 *
 * Behaviour:
 *   • Catches descendant render/lifecycle errors via `onErrorCaptured`.
 *   • On catch: stores the error, REPORTS it (errorReporter, source
 *     "vue-render"), and returns `false` to STOP propagation — the root tree
 *     survives, only this slot shows the fallback.
 *   • Shows a centered, theme-safe fallback card («Не удалось отобразить
 *     страницу») with «Обновить страницу» (reload) and «Назад» (router.back).
 *     In DEV the error message is shown in a <pre> for debugging.
 *   • RESETS on route change (watch fullPath) so navigating away from a broken
 *     page recovers automatically — no manual reload needed.
 *
 * Note: an error boundary only catches errors thrown SYNCHRONOUSLY during a
 * descendant's render/lifecycle. Async errors (event handlers, promises) are
 * handled separately by the global handlers wired in main.ts.
 */
import { ref, watch, onErrorCaptured } from "vue";
import { useRoute, useRouter } from "vue-router";
import { reportClientError } from "../../lib/errorReporter";

const route = useRoute();
const router = useRouter();

/** The captured error (null when the slot is rendering normally). */
const captured = ref<Error | null>(null);

onErrorCaptured((err, _instance, info) => {
  // Store so the template swaps to the fallback.
  captured.value = err instanceof Error ? err : new Error(String(err));

  // Best-effort report — reportClientError never throws.
  reportClientError({
    message: captured.value.message,
    stack: captured.value.stack,
    info,
    source: "vue-render",
  });

  // Stop propagation: keep the rest of the app (shell, nav) alive instead of
  // letting Vue unmount the whole root tree.
  return false;
});

// Recover on navigation — clearing the error re-renders the (now different)
// slot. Without this, a user who hit a broken page would be stuck on the
// fallback even after clicking a nav link.
watch(
  () => route.fullPath,
  () => {
    captured.value = null;
  },
);

function reload(): void {
  location.reload();
}

function goBack(): void {
  router.back();
}

const isDev = import.meta.env.DEV;
</script>

<template>
  <div
    v-if="captured"
    data-test="route-error-boundary"
    class="flex min-h-[60vh] items-center justify-center px-4 py-10"
  >
    <div class="card max-w-md w-full p-8 text-center">
      <h1 class="text-lg font-semibold text-surface-900">
        Не удалось отобразить страницу
      </h1>
      <p class="mt-2 text-sm text-surface-500">
        Произошла ошибка. Попробуйте обновить страницу.
      </p>

      <div class="mt-6 flex items-center justify-center gap-3">
        <button class="btn-primary" @click="reload">Обновить страницу</button>
        <button class="btn-secondary" @click="goBack">Назад</button>
      </div>

      <pre
        v-if="isDev"
        class="mt-6 max-h-48 overflow-auto rounded-lg border border-surface-200 bg-surface-50 p-3 text-left text-xs text-danger-600 whitespace-pre-wrap"
      >{{ captured.message }}</pre>
    </div>
  </div>
  <slot v-else />
</template>
